/**
 * DEMO-ONLY HTTP handlers for ICM wishlist / order-template mutations (Presidio LOCAL_MOCK).
 * Covers share, optional lines, product add/remove, and session-created saved carts
 * that stay visible across users of the same customer account (FR8.2.2).
 */
import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

import {
  addPresidioOrderTemplateProduct,
  addPresidioOrderTemplateShare,
  buildPresidioCreatedOrderTemplateBody,
  createPresidioOrderTemplate,
  deletePresidioCreatedOrderTemplate,
  getPresidioCreatedOrderTemplate,
  removePresidioOrderTemplateProduct,
  removePresidioOrderTemplateShare,
  setPresidioOrderTemplateOptional,
  updatePresidioCreatedOrderTemplateTitle,
  type PresidioOrderTemplateShareRecipient,
} from './presidio-mock-order-templates';

const WISHLIST_COLLECTION = /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)$/;
const WISHLIST_DETAIL = /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)\/([^/]+)$/;
const WISHLIST_SHARE = /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)\/([^/]+)\/share(?:\/([^/]+))?$/;
const WISHLIST_OPTIONAL =
  /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)\/([^/]+)\/products\/([^/]+)\/optional$/;
const WISHLIST_PRODUCT = /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)\/([^/]+)\/products\/([^/]+)$/;

export function handlePresidioMockOrderTemplates(
  req: HttpRequest<unknown>,
  restPath: string,
  readJsonBody: <T>(body: unknown) => T | null
): Observable<HttpEvent<unknown>> | null {
  const method = req.method.toUpperCase();

  const shareMatch = WISHLIST_SHARE.exec(restPath);
  if (shareMatch) {
    const templateId = shareMatch[1];
    const recipientId = shareMatch[2];
    if (method === 'POST') {
      const body =
        readJsonBody<PresidioOrderTemplateShareRecipient>(req.body) ||
        ({ id: '', displayName: '', type: 'account_user' } as PresidioOrderTemplateShareRecipient);
      if (!body.id) {
        return throwError(
          () => new HttpErrorResponse({ status: 400, error: { error: 'recipient id required' }, url: req.url })
        );
      }
      const sharedWith = addPresidioOrderTemplateShare(templateId, body);
      return of(new HttpResponse({ status: 200, body: { sharedWith }, url: req.url }));
    }
    if (method === 'DELETE' && recipientId) {
      const sharedWith = removePresidioOrderTemplateShare(templateId, recipientId);
      return of(new HttpResponse({ status: 200, body: { sharedWith }, url: req.url }));
    }
    return null;
  }

  const optionalMatch = WISHLIST_OPTIONAL.exec(restPath);
  if (optionalMatch && method === 'PUT') {
    const templateId = optionalMatch[1];
    const sku = decodeURIComponent(optionalMatch[2]);
    const body = readJsonBody<{ optional?: boolean }>(req.body) || {};
    setPresidioOrderTemplateOptional(templateId, sku, !!body.optional);
    return of(new HttpResponse({ status: 200, body: { optional: !!body.optional }, url: req.url }));
  }

  const productMatch = WISHLIST_PRODUCT.exec(restPath);
  if (productMatch) {
    const templateId = productMatch[1];
    const sku = decodeURIComponent(productMatch[2]);
    if (method === 'POST') {
      const quantity = Number(req.params.get('quantity') || 1) || 1;
      addPresidioOrderTemplateProduct(templateId, sku, quantity);
      return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
    }
    if (method === 'DELETE') {
      removePresidioOrderTemplateProduct(templateId, sku);
      return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
    }
  }

  if (WISHLIST_COLLECTION.test(restPath) && method === 'POST') {
    const body = readJsonBody<{ title?: string }>(req.body) || {};
    const link = createPresidioOrderTemplate(body.title || '');
    return of(new HttpResponse({ status: 201, body: link, url: req.url }));
  }

  const detailMatch = WISHLIST_DETAIL.exec(restPath);
  if (detailMatch) {
    const templateId = decodeURIComponent(detailMatch[1]);
    if (!getPresidioCreatedOrderTemplate(templateId)) {
      return null;
    }
    if (method === 'GET') {
      const body = buildPresidioCreatedOrderTemplateBody(templateId);
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }
    if (method === 'PUT') {
      const body = readJsonBody<{ title?: string }>(req.body) || {};
      const updated = updatePresidioCreatedOrderTemplateTitle(templateId, body.title || '');
      const detail = buildPresidioCreatedOrderTemplateBody(templateId);
      return of(
        new HttpResponse({
          status: 200,
          body: detail || { title: updated?.title, creationDate: updated?.creationDate },
          url: req.url,
        })
      );
    }
    if (method === 'DELETE') {
      deletePresidioCreatedOrderTemplate(templateId);
      return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
    }
  }

  return null;
}
